import { css } from '@emotion/react'
import { Button, Flow, HeadingSection } from 'bold-ui'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { Breadcrumb } from 'components/breadcrumb'
import { PecFormApi, resetAllFieldStates } from 'components/form'
import { InfoIcon } from 'components/InfoIcon'
import { PageContent } from 'components/layout/PageContent'
import { useLoadImoveisDuplicadosLazyQuery, useLoadImoveisInativacaoLazyQuery } from 'graphql/hooks.generated'
import { ImoveisDuplicadosQueryInput, ImoveisQueryInput, TipoEnderecoEnum } from 'graphql/types.generated'
import { useEffect, useMemo, useRef, useState } from 'react'
import { v4 } from 'uuid'

import { CardNovidadesInativacaoLoteImoveis } from './components/CardNovidadesInativacaoLoteImoveis'
import { InativacaoImoveisTable } from './components/InativacaoImoveisTable'
import { InativacaoImoveisBuscaForm } from './InativacaoImoveisBuscaForm'
import { IMOVEIS_DUPLICADOS_TOOLTIP_TEXT, InativacaoImoveisBuscaFormModel } from './model-inativacaoImoveis'

const FORA_DE_AREA = 'FA'
const INITIAL_VALUES: ImoveisQueryInput = {
  tipoEndereco: TipoEnderecoEnum.LOGRADOURO,
  stAtivo: true,
  pageParams: {
    page: 0,
    size: 10,
    sort: ['bairro', 'logradouro', 'numero', 'complemento', 'id'],
  },
}

export function InativacaoImoveisView() {
  const { acesso } = useAcessoLotacaoOrEstagio()
  const formRef = useRef<PecFormApi<InativacaoImoveisBuscaFormModel>>()
  const [imoveisInput, setImoveisInput] = useState<ImoveisQueryInput>(INITIAL_VALUES)
  const [resultadoVisivel, setResultadoVisivel] = useState(false)
  const [processingState, setProcessingState] = useState(false)
  const [progressToken, setProgressToken] = useState(v4())
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set())
  const [sugestoesVisiveis, setSugestoesVisiveis] = useState(false)
  const [loadImoveis, { data, loading, refetch }] = useLoadImoveisInativacaoLazyQuery()
  const [
    loadImoveisDuplicados,
    { data: dataDuplicados, loading: loadingDuplicados, refetch: refetchDuplicados },
  ] = useLoadImoveisDuplicadosLazyQuery()

  const duplicadosInput: ImoveisDuplicadosQueryInput = useMemo(
    () => ({
      cnes: acesso.unidadeSaude.cnes,
      ine: acesso.equipe?.ine,
      pageParams: imoveisInput.pageParams,
    }),
    [acesso.equipe, acesso.unidadeSaude.cnes, imoveisInput.pageParams]
  )

  useEffect(() => {
    if (resultadoVisivel) {
      sugestoesVisiveis
        ? loadImoveisDuplicados({ variables: { input: duplicadosInput } })
        : loadImoveis({ variables: { input: imoveisInput } })
    }
  }, [duplicadosInput, imoveisInput, loadImoveis, loadImoveisDuplicados, resultadoVisivel, sugestoesVisiveis])

  const pageResult = sugestoesVisiveis ? dataDuplicados?.imoveisDuplicados : data?.imoveis

  const handleRefetch = () =>
    sugestoesVisiveis ? refetchDuplicados({ input: duplicadosInput }) : refetch({ input: imoveisInput })

  const onBuscarImoveis = (formValues: InativacaoImoveisBuscaFormModel) => {
    const { unidadeSaude, equipe, microarea, ocultarImoveisComFamilia, ...values } = formValues

    const input = {
      ...values,
      tipoEndereco: TipoEnderecoEnum.LOGRADOURO,
      cnes: unidadeSaude?.cnes,
      ine: equipe?.ine,
      ocultarFamilias: ocultarImoveisComFamilia,
      microarea: microarea !== FORA_DE_AREA ? microarea : undefined,
      foraDeArea: microarea === FORA_DE_AREA,
      stAtivo: true,
      pageParams: {
        page: 0,
        size: 10,
        sort: ['bairro', 'logradouro', 'numero', 'complemento', 'id'],
      },
    }

    setImoveisInput(input)
    setSugestoesVisiveis(false)
    setResultadoVisivel(true)
    setProcessingState(false)
    setCheckedItems(new Set())
  }

  const onBuscarSugestoes = () => {
    if (formRef.current) {
      resetAllFieldStates(formRef.current)
      formRef.current.reset({ ocultarImoveisComFamilia: true })
    }
    setImoveisInput({ pageParams: INITIAL_VALUES.pageParams })
    setSugestoesVisiveis(true)
    setResultadoVisivel(true)
    setProcessingState(false)
    setCheckedItems(new Set())
  }

  const onLimparCampos = () => {
    setImoveisInput(INITIAL_VALUES)
    setSugestoesVisiveis(false)
    setResultadoVisivel(false)
    setProcessingState(false)
    setCheckedItems(new Set())
  }

  const resetProcessingState = () => {
    setProcessingState(false)
    setProgressToken(v4())
  }

  return (
    <PageContent fluid type='filled'>
      <Breadcrumb title='Inativação de imóveis' />
      <Flow direction='horizontal' justifyContent='space-between' alignItems='center'>
        <HeadingSection level={1} title='Inativação de imóveis' />
        <Flow direction='horizontal' gap={0.5} alignItems='center'>
          <InfoIcon icon='infoCircleFilled' text={IMOVEIS_DUPLICADOS_TOOLTIP_TEXT} />
          <Button kind='primary' skin='outline' size='small' onClick={onBuscarSugestoes} disabled={processingState}>
            Buscar imóveis duplicados
          </Button>
        </Flow>
      </Flow>
      <Flow direction='vertical'>
        <CardNovidadesInativacaoLoteImoveis />
        <InativacaoImoveisBuscaForm
          formRef={formRef}
          onSubmit={onBuscarImoveis}
          onLimparCampos={onLimparCampos}
          disabled={processingState}
        />
        {resultadoVisivel && pageResult && (
          <div css={styles.resultContainer}>
            <InativacaoImoveisTable
              pageResult={pageResult}
              loading={loading || loadingDuplicados}
              setImoveisInput={setImoveisInput}
              refetch={handleRefetch}
              checkedItems={checkedItems}
              setCheckedItems={setCheckedItems}
              progressToken={progressToken}
              processingState={processingState}
              changeToProcessingState={() => setProcessingState(true)}
              resetProcessingState={resetProcessingState}
            />
          </div>
        )}
      </Flow>
    </PageContent>
  )
}

const styles = {
  resultContainer: css`
    margin-top: 56px;
  `,
}
